import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';

import defaultClient from '@src/api/clients/default';
import { fetchClients } from '@src/api/actions/clients';
import { fetchClientVerticalOptions } from '@src/api/actions/verticals';
import {
  previewFacebookZipTargeting,
  pushFacebookZipTargeting,
  fetchFacebookAdSetTargeting,
  fetchFacebookZipPushLog,
} from '@src/api/actions';
import { useMessage } from '@src/context/common';

function parseCampaignVertical(campaignName) {
  if (!campaignName) {
    return null;
  }
  try {
    const normalized = campaignName.startsWith('?') ? campaignName : `?${campaignName}`;
    const params = new URLSearchParams(normalized);
    const vertical = params.get('v') || params.get('vertical');
    return vertical ? vertical.trim() : null;
  } catch (error) {
    return null;
  }
}

const PREVIEW_SAMPLE_SIZE = 20;

function CampaignBuilderPage() {
  const message = useMessage();

  const [clients, setClients] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(true);
  const [clientsError, setClientsError] = useState('');

  const [selectedClientId, setSelectedClientId] = useState(null);
  const [structure, setStructure] = useState([]);
  const [structureLoading, setStructureLoading] = useState(false);
  const [structureError, setStructureError] = useState('');

  const [territories, setTerritories] = useState([]);
  const [territoriesLoading, setTerritoriesLoading] = useState(false);
  const [territoriesError, setTerritoriesError] = useState('');

  const [verticals, setVerticals] = useState([]);
  const [verticalsLoading, setVerticalsLoading] = useState(true);
  const [verticalsError, setVerticalsError] = useState('');

  const [selectedAdAccountId, setSelectedAdAccountId] = useState('');
  const [selectedCampaignIds, setSelectedCampaignIds] = useState([]);
  const [selectedAdSetIds, setSelectedAdSetIds] = useState([]);
  const [showInactiveCampaigns, setShowInactiveCampaigns] = useState(false);

  const [selectedVerticalIds, setSelectedVerticalIds] = useState([]);
  const [selectedTerritoryIds, setSelectedTerritoryIds] = useState([]);
  const [includeFilteredOut, setIncludeFilteredOut] = useState(false);

  const [preview, setPreview] = useState({ zip_count: 0, sample_zip_codes: [] });
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');
  const [adSetTargeting, setAdSetTargeting] = useState(null);
  const [adSetTargetingLoading, setAdSetTargetingLoading] = useState(false);
  const [adSetTargetingError, setAdSetTargetingError] = useState('');
  const [pushLog, setPushLog] = useState([]);
  const [pushLogLoading, setPushLogLoading] = useState(false);
  const [pushLogError, setPushLogError] = useState('');

  const [pushLoading, setPushLoading] = useState(false);
  const [pushError, setPushError] = useState('');
  const [frontendLogs, setFrontendLogs] = useState([]);

  const appendFrontendLog = (messageText, meta = {}) => {
    setFrontendLogs((prev) => [
      {
        id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
        at: new Date().toISOString(),
        message: messageText,
        meta,
      },
      ...prev,
    ].slice(0, 25));
  };

  useEffect(() => {
    setClientsLoading(true);
    fetchClients()
      .then((response) => {
        setClients(response.data);
      })
      .catch((error) => {
        setClientsError(error?.message || 'Failed to load clients.');
      })
      .finally(() => setClientsLoading(false));
  }, []);

  useEffect(() => {
    if (!selectedClientId) {
      setVerticals([]);
      setSelectedVerticalIds([]);
      return;
    }

    setVerticalsLoading(true);
    setVerticalsError('');
    fetchClientVerticalOptions({ clientId: selectedClientId })
      .then((response) => {
        const fetched = response.data || [];
        setVerticals(fetched);
        setSelectedVerticalIds(fetched.map((vertical) => vertical.id));
      })
      .catch((error) => {
        setVerticalsError(error?.message || 'Failed to load verticals.');
      })
      .finally(() => setVerticalsLoading(false));
  }, [selectedClientId]);

  useEffect(() => {
    if (!selectedClientId) {
      setStructure([]);
      setSelectedAdAccountId('');
      setSelectedCampaignIds([]);
      setSelectedAdSetIds([]);
      return;
    }
    setStructureLoading(true);
    setStructureError('');
    defaultClient
      .request({
        url: `dashboard/facebook/ad-structure/?client_id=${selectedClientId}&include_inactive=${showInactiveCampaigns}`,
        method: 'GET',
      })
      .then((response) => {
        const fetchedStructure = response.data || [];
        setStructure(fetchedStructure);
        const campaignCount = fetchedStructure.reduce(
          (sum, account) => sum + ((account?.campaigns || []).length),
          0
        );
        const adSetCount = fetchedStructure.reduce(
          (sum, account) =>
            sum +
            (account?.campaigns || []).reduce(
              (campaignSum, campaign) => campaignSum + ((campaign?.ad_sets || []).length),
              0
            ),
          0
        );
        appendFrontendLog('Ad structure loaded', {
          accounts: fetchedStructure.length,
          campaigns: campaignCount,
          ad_sets: adSetCount,
          include_inactive: showInactiveCampaigns,
        });
      })
      .catch((error) => {
        setStructureError(error?.message || 'Failed to load Facebook structure.');
        appendFrontendLog('Ad structure failed', {
          error: error?.message || 'Failed to load Facebook structure.',
          client_id: selectedClientId,
          include_inactive: showInactiveCampaigns,
        });
      })
      .finally(() => setStructureLoading(false));
  }, [selectedClientId, showInactiveCampaigns]);

  useEffect(() => {
    if (!selectedClientId) {
      setTerritories([]);
      return;
    }
    setTerritoriesLoading(true);
    setTerritoriesError('');
    setSelectedTerritoryIds([]);
    defaultClient
      .request({
        url: `dashboard/client-territories/?client_id=${selectedClientId}`,
        method: 'GET',
      })
      .then((response) => {
        setTerritories(response.data);
      })
      .catch((error) => {
        setTerritoriesError(error?.message || 'Failed to load territories.');
      })
      .finally(() => setTerritoriesLoading(false));
  }, [selectedClientId]);

  useEffect(() => {
    if (!territories.length) {
      setSelectedTerritoryIds([]);
      return;
    }
    setSelectedTerritoryIds(territories.map((territory) => territory.id));
  }, [territories]);

  useEffect(() => {
    if (!structure.length) {
      setSelectedAdAccountId('');
      return;
    }
    const defaultAccount = structure[0];
    setSelectedAdAccountId((prev) => prev || defaultAccount.account_id);
  }, [structure]);

  const selectedAccount = useMemo(
    () => structure.find((account) => account.account_id === selectedAdAccountId) || null,
    [structure, selectedAdAccountId]
  );

  useEffect(() => {
    const campaigns = selectedAccount?.campaigns || [];
    if (!campaigns.length) {
      setSelectedCampaignIds([]);
      return;
    }
    setSelectedCampaignIds((prev) => {
      const validPrev = prev.filter((id) =>
        campaigns.some((campaign) => campaign.campaign_id === id)
      );
      if (validPrev.length) {
        return validPrev;
      }
      const firstWithAdSets = campaigns.find(
        (campaign) => (campaign?.ad_sets || []).length > 0
      );
      return [firstWithAdSets?.campaign_id || campaigns[0].campaign_id];
    });
  }, [selectedAccount]);

  const selectedCampaigns = useMemo(
    () =>
      (selectedAccount?.campaigns || []).filter((campaign) =>
        selectedCampaignIds.includes(campaign.campaign_id)
      ),
    [selectedAccount, selectedCampaignIds]
  );

  const adSetOptions = useMemo(() => {
    const seen = {};
    selectedCampaigns.forEach((campaign) => {
      (campaign.ad_sets || []).forEach((adSet) => {
        if (adSet?.ad_set_id && !seen[adSet.ad_set_id]) {
          seen[adSet.ad_set_id] = adSet;
        }
      });
    });
    return Object.values(seen);
  }, [selectedCampaigns]);

  useEffect(() => {
    if (!selectedCampaigns.length || !verticals.length) {
      return;
    }
    const targetVertNames = new Set();
    selectedCampaigns.forEach((campaign) => {
      const verticalName = parseCampaignVertical(campaign.campaign_name);
      if (verticalName) {
        targetVertNames.add(verticalName.toLowerCase());
      }
    });
    if (!targetVertNames.size) {
      return;
    }
    const matched = verticals.filter((vertical) =>
      targetVertNames.has((vertical.vertical || '').toLowerCase())
    );
    if (!matched.length) {
      return;
    }
    setSelectedVerticalIds(matched.map((vertical) => vertical.id));
  }, [selectedCampaigns, verticals]);

  useEffect(() => {
    if (!adSetOptions.length) {
      setSelectedAdSetIds([]);
      return;
    }
    setSelectedAdSetIds((prev) => {
      const filtered = prev.filter((id) =>
        adSetOptions.some((adSet) => adSet.ad_set_id === id)
      );
      return filtered.length ? filtered : [adSetOptions[0].ad_set_id];
    });
  }, [adSetOptions]);

  useEffect(() => {
    if (!selectedClientId) {
      setPreview({ zip_count: 0, sample_zip_codes: [] });
      setPreviewError('');
      return;
    }

    setPreviewLoading(true);
    setPreviewError('');

    previewFacebookZipTargeting({
      clientId: selectedClientId,
      verticalIds: selectedVerticalIds,
      territoryIds: selectedTerritoryIds,
      includeFilteredOut,
      sampleSize: PREVIEW_SAMPLE_SIZE,
    })
      .then((response) => {
        setPreview(response.data);
      })
      .catch((error) => {
        setPreviewError(error?.message || 'Failed to preview ZIPs.');
        setPreview({ zip_count: 0, sample_zip_codes: [] });
      })
      .finally(() => {
        setPreviewLoading(false);
      });
  }, [selectedClientId, selectedVerticalIds, selectedTerritoryIds, includeFilteredOut]);

  useEffect(() => {
    if (!selectedClientId || !selectedAdAccountId || selectedAdSetIds.length !== 1) {
      setAdSetTargeting(null);
      setAdSetTargetingError('');
      return;
    }

    const adSetId = selectedAdSetIds[0];
    setAdSetTargetingLoading(true);
    setAdSetTargetingError('');
    fetchFacebookAdSetTargeting({
      clientId: selectedClientId,
      adAccountId: selectedAdAccountId,
      adSetId,
    })
      .then((response) => {
        setAdSetTargeting(response.data);
      })
      .catch((error) => {
        setAdSetTargeting(null);
        setAdSetTargetingError(error?.message || 'Failed to load ad set targeting.');
      })
      .finally(() => setAdSetTargetingLoading(false));
  }, [selectedClientId, selectedAdAccountId, selectedAdSetIds]);

  const loadPushLog = (options = {}) => {
    if (!selectedClientId) {
      setPushLog([]);
      return;
    }
    setPushLogLoading(true);
    setPushLogError('');
    fetchFacebookZipPushLog({
      clientId: selectedClientId,
      adSetId: options.adSetId,
      limit: options.limit || 25,
    })
      .then((response) => {
        setPushLog(response.data || []);
      })
      .catch((error) => {
        setPushLog([]);
        setPushLogError(error?.message || 'Failed to load push log.');
      })
      .finally(() => setPushLogLoading(false));
  };

  useEffect(() => {
    loadPushLog();
  }, [selectedClientId]);

  const handlePush = async () => {
    if (!selectedClientId || !selectedAdAccountId || !selectedAdSetIds.length) {
      return;
    }

    setPushLoading(true);
    setPushError('');
    appendFrontendLog('Push started', {
      client_id: selectedClientId,
      ad_account_id: selectedAdAccountId,
      ad_set_ids: selectedAdSetIds,
      include_filtered_out: includeFilteredOut,
      preview_zip_count: preview.zip_count || 0,
    });

    try {
      const selectedAdSets = adSetOptions.filter((adSet) =>
        selectedAdSetIds.includes(adSet.ad_set_id)
      );
      const adSetNames = selectedAdSets.map((adSet) => adSet.ad_set_name || '');
      const response = await pushFacebookZipTargeting({
        client_id: selectedClientId,
        ad_account_id: selectedAdAccountId,
        ad_set_ids: selectedAdSetIds,
        ad_set_names: adSetNames,
        vertical_ids: selectedVerticalIds,
        territory_ids: selectedTerritoryIds,
        include_filtered_out: includeFilteredOut,
        source_type: 'campaign_builder_ui',
        source_id: `campaign-builder-${Date.now()}`,
        csv_name: `Campaign Builder ${new Date().toISOString()}`,
      });

      const resultCount =
        response?.data?.results?.length || selectedAdSetIds.length;
      appendFrontendLog('Push succeeded', {
        ad_account_id: response?.data?.ad_account_id || selectedAdAccountId,
        ad_set_count: resultCount,
        requested_zip_count: response?.data?.zip_count,
        pushed_zip_count: response?.data?.pushed_zip_count,
        invalid_zip_codes: response?.data?.invalid_zip_codes || [],
      });
      message.success(
        `ZIP targeting pushed to ${resultCount} ad set${resultCount === 1 ? '' : 's'} (${preview.zip_count || response.data.zip_count} zips).`
      );
      loadPushLog({ adSetId: selectedAdSetIds.length === 1 ? selectedAdSetIds[0] : undefined });
    } catch (error) {
      const detail = error?.response?.data?.detail || error?.message || 'Failed to push ZIP targeting.';
      setPushError(detail);
      appendFrontendLog('Push failed', {
        error: detail,
        client_id: selectedClientId,
        ad_account_id: selectedAdAccountId,
        ad_set_ids: selectedAdSetIds,
      });
      message.error(detail);
    } finally {
      setPushLoading(false);
    }
  };

  const accountOptions = structure;
  const campaignOptions = selectedAccount?.campaigns || [];

  const previewChips = preview.sample_zip_codes.map((zip) => (
    <Chip key={zip} label={zip} size="small" />
  ));

  return (
    <Box>
      <Stack spacing={3}>
        <Card>
          <CardHeader title="Campaign Builder" subheader="Select zips and push them to Facebook ad sets" />
          <Divider />
          <CardContent>
            <Stack spacing={3}>
              {clientsError && <Alert severity="error">{clientsError}</Alert>}
              <Stack
                marginTop={1}
                direction={{ xs: 'column', md: 'row' }}
                spacing={2}
                sx={{ alignItems: { xs: 'stretch', md: 'flex-start' } }}
              >
                <Box sx={{ flex: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FormControl fullWidth>
                    <InputLabel id="client-select-label">Client</InputLabel>
                    <Select
                      labelId="client-select-label"
                      value={selectedClientId || ''}
                      label="Client"
                      onChange={(event) => setSelectedClientId(event.target.value)}
                      disabled={clientsLoading}
                    >
                      {clients.map((client) => (
                        <MenuItem key={client.id} value={client.id}>
                          {client.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {clientsLoading && <CircularProgress size={18} />}
                </Box>
                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FormControl fullWidth>
                    <InputLabel id="ad-account-select-label">Facebook Ad Account</InputLabel>
                    <Select
                      labelId="ad-account-select-label"
                      value={selectedAdAccountId || ''}
                      label="Facebook Ad Account"
                      onChange={(event) => setSelectedAdAccountId(event.target.value)}
                      disabled={structureLoading || !accountOptions.length}
                    >
                      {accountOptions.map((account) => (
                        <MenuItem key={account.account_id} value={account.account_id}>
                          {account.account_name || account.account_id}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {structureLoading && <CircularProgress size={18} />}
                </Box>
              </Stack>
              <Stack marginTop={1} direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1 }}>
                  <FormControl fullWidth>
                    <InputLabel id="campaign-select-label">Campaign</InputLabel>
                    <Select
                      labelId="campaign-select-label"
                      multiple
                      value={selectedCampaignIds}
                      label="Campaign"
                      onChange={(event) => setSelectedCampaignIds(event.target.value)}
                      disabled={!campaignOptions.length || structureLoading}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((id) => {
                            const campaign = campaignOptions.find(
                              (item) => item.campaign_id === id
                            );
                            const label = campaign
                              ? `${campaign.campaign_name || `Campaign ${campaign.campaign_id}`} ${
                                  campaign.status ? `(${campaign.status})` : ''
                                }`.trim()
                              : id;
                            return (
                              <Chip key={id} label={label} size="small" />
                            );
                          })}
                        </Box>
                      )}
                    >
                      {campaignOptions.map((campaign) => {
                        const label = `${campaign.campaign_name || `Campaign ${campaign.campaign_id}`} ${
                          campaign.status ? `(${campaign.status})` : ''
                        }`.trim();
                        return (
                          <MenuItem key={campaign.campaign_id} value={campaign.campaign_id}>
                            <Checkbox checked={selectedCampaignIds.includes(campaign.campaign_id)} />
                            <ListItemText primary={label} />
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                  {structureLoading && <CircularProgress size={18} />}
                </Stack>
                <FormControlLabel
                  control={
                    <Switch
                      checked={showInactiveCampaigns}
                      onChange={(event) => setShowInactiveCampaigns(event.target.checked)}
                      size="small"
                    />
                  }
                  label="Show inactive campaigns"
                  sx={{ alignSelf: 'center', marginLeft: 1 }}
                />
                <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1 }}>
                  <FormControl fullWidth>
                    <InputLabel id="ad-set-select-label">Ad Set</InputLabel>
                    <Select
                      labelId="ad-set-select-label"
                      multiple
                      value={selectedAdSetIds}
                      label="Ad Set"
                      onChange={(event) => setSelectedAdSetIds(event.target.value)}
                      disabled={!adSetOptions.length || structureLoading}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((id) => {
                            const adSet = adSetOptions.find((item) => item.ad_set_id === id);
                            const label = adSet
                              ? `${adSet.ad_set_name || `Ad Set ${adSet.ad_set_id}`} ${
                                  adSet.active ? '' : '(Inactive)'
                                }`.trim()
                              : id;
                            return <Chip key={id} label={label} size="small" />;
                          })}
                        </Box>
                      )}
                    >
                      {adSetOptions.map((adSet) => {
                        const label = `${adSet.ad_set_name || `Ad Set ${adSet.ad_set_id}`} ${
                          adSet.active ? '' : '(Inactive)'
                        }`.trim();
                        return (
                          <MenuItem key={adSet.ad_set_id} value={adSet.ad_set_id}>
                            <Checkbox checked={selectedAdSetIds.includes(adSet.ad_set_id)} />
                            <ListItemText primary={label} />
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                  {structureLoading && <CircularProgress size={18} />}
                </Stack>
              </Stack>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={2}
                sx={{ alignItems: 'stretch' }}
              >
                <Autocomplete
                  multiple
                  options={verticals}
                  getOptionLabel={(option) => option.vertical || ''}
                  value={verticals.filter((vert) => selectedVerticalIds.includes(vert.id))}
                  onChange={(_, value) => setSelectedVerticalIds(value.map((item) => item.id))}
                  loading={verticalsLoading}
                  disableCloseOnSelect
                  sx={{ flex: 1, minWidth: 200 }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Verticals"
                      placeholder="Select verticals"
                      fullWidth
                    />
                  )}
                />
                <Autocomplete
                  multiple
                  options={territories}
                  getOptionLabel={(option) => option.name || option.territory || ''}
                  value={territories.filter((territory) => selectedTerritoryIds.includes(territory.id))}
                  onChange={(_, value) => setSelectedTerritoryIds(value.map((item) => item.id))}
                  loading={territoriesLoading}
                  disableCloseOnSelect
                  sx={{ flex: 1, minWidth: 200 }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Territories"
                      placeholder="Select territories"
                      fullWidth
                    />
                  )}
                />
              </Stack>
              <Stack direction="row" spacing={1} sx={{ marginTop: 1 }}>
                <Button
                  color="primary"
                  size="small"
                  variant="outlined"
                  onClick={() => setSelectedTerritoryIds(territories.map((territory) => territory.id))}
                  disabled={!territories.length}
                >
                  All Territories
                </Button>
                <Button
                  color="primary"
                  size="small"
                  variant="text"
                  onClick={() => setSelectedTerritoryIds([])}
                  disabled={!selectedTerritoryIds.length}
                >
                  Clear selection
                </Button>
              </Stack>
              <FormControlLabel
                control={
                  <Switch
                    checked={includeFilteredOut}
                    onChange={(event) => setIncludeFilteredOut(event.target.checked)}
                  />
                }
                label="Include filtered-out ZIPs"
              />
              <Divider />
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                <Stack>
                  <Typography variant="subtitle2">ZIP preview</Typography>
                  {previewLoading ? (
                    <CircularProgress size={20} />
                  ) : preview.zip_count ? (
                    <Typography variant="h5">{preview.zip_count.toLocaleString()} ZIPs selected</Typography>
                  ) : (
                    <Typography color="text.secondary">No ZIPs selected yet.</Typography>
                  )}
                </Stack>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handlePush}
                  disabled={
                    !selectedClientId ||
                    !selectedAdAccountId ||
                    !selectedAdSetIds.length ||
                    pushLoading ||
                    !preview.zip_count
                  }
                  endIcon={pushLoading ? <CircularProgress size={20} /> : null}
                >
                  Push ZIPs to Facebook
                </Button>
              </Stack>
              <Divider />
              <Stack spacing={1}>
                <Typography variant="subtitle2">Current selected Ad Set targeting</Typography>
                {selectedAdSetIds.length !== 1 ? (
                  <Typography color="text.secondary">
                    Select exactly one ad set to inspect its current Facebook targeting.
                  </Typography>
                ) : adSetTargetingLoading ? (
                  <CircularProgress size={20} />
                ) : adSetTargeting ? (
                  <>
                    <Typography variant="body2" color="text.secondary">
                      Direct zips: {adSetTargeting.direct_zip_count} | Cluster zips (from DB):{' '}
                      {adSetTargeting.db_cluster_zip_count}
                    </Typography>
                    {adSetTargeting.facebook_location_cluster_ids?.length ? (
                      <Typography variant="body2" color="text.secondary">
                        Facebook Cluster IDs: {adSetTargeting.facebook_location_cluster_ids.join(', ')}
                      </Typography>
                    ) : null}
                    {adSetTargeting.db_location_cluster_ids?.length ? (
                      <Typography variant="body2" color="text.secondary">
                        DB Cluster IDs: {adSetTargeting.db_location_cluster_ids.join(', ')}
                      </Typography>
                    ) : null}
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {(adSetTargeting.direct_zip_codes || []).slice(0, 20).map((zip) => (
                        <Chip key={`direct-${zip}`} label={zip} size="small" />
                      ))}
                      {(adSetTargeting.db_cluster_zip_codes || []).slice(0, 20).map((zip) => (
                        <Chip key={`cluster-${zip}`} label={zip} size="small" variant="outlined" />
                      ))}
                    </Stack>
                  </>
                ) : (
                  <Typography color="text.secondary">No targeting data found for this ad set.</Typography>
                )}
              </Stack>
              {previewError && <Alert severity="error">{previewError}</Alert>}
              {adSetTargetingError && <Alert severity="error">{adSetTargetingError}</Alert>}
              {pushError && <Alert severity="error">{pushError}</Alert>}
              <Divider />
              <Stack spacing={1}>
                <Typography variant="subtitle2">Session debug log</Typography>
                {frontendLogs.length ? (
                  <Stack spacing={1}>
                    {frontendLogs.map((entry) => (
                      <Box
                        key={entry.id}
                        sx={{
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          p: 1,
                        }}
                      >
                        <Typography variant="body2">
                          {new Date(entry.at).toLocaleString()} • {entry.message}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                        >
                          {JSON.stringify(entry.meta)}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                ) : (
                  <Typography color="text.secondary">No session logs yet.</Typography>
                )}
              </Stack>
              <Divider />
              <Stack spacing={1}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Typography variant="subtitle2">ZIP push log</Typography>
                  <Button
                    size="small"
                    variant="text"
                    onClick={() =>
                      loadPushLog({
                        adSetId: selectedAdSetIds.length === 1 ? selectedAdSetIds[0] : undefined,
                      })
                    }
                    disabled={pushLogLoading || !selectedClientId}
                  >
                    Refresh
                  </Button>
                </Stack>
                {pushLogLoading ? (
                  <CircularProgress size={20} />
                ) : pushLogError ? (
                  <Alert severity="error">{pushLogError}</Alert>
                ) : pushLog.length ? (
                  <Stack spacing={1}>
                    {pushLog.map((entry) => (
                      <Stack
                        key={`${entry.ad_set_id}-${entry.updated_at}`}
                        direction={{ xs: 'column', md: 'row' }}
                        spacing={1}
                        justifyContent="space-between"
                      >
                        <Stack spacing={0.5}>
                          <Typography variant="body2">
                            {(entry.ad_account_name || entry.ad_account_id || 'Ad Account')} •{' '}
                            {entry.campaign_name || entry.campaign_id || 'Campaign'} •{' '}
                            {entry.ad_set_name || entry.ad_set_id}
                          </Typography>
                          {(entry.verticals?.length || entry.territories?.length) ? (
                            <Typography variant="caption" color="text.secondary">
                              {(entry.verticals || []).join(', ') || 'No verticals'} •{' '}
                              {(entry.territories || []).join(', ') || 'No territories'}
                            </Typography>
                          ) : null}
                        </Stack>
                        <Typography variant="body2" color="text.secondary">
                          {entry.zip_count} zips • {entry.cluster_id || 'no cluster'} •{' '}
                          {entry.updated_at
                            ? new Date(entry.updated_at).toLocaleString()
                            : 'unknown time'}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>
                ) : (
                  <Typography color="text.secondary">No pushes recorded yet.</Typography>
                )}
              </Stack>
              {preview.sample_zip_codes.length > 0 && (
                <Stack direction="row" flexWrap="wrap" gap={1}>
                  {previewChips}
                </Stack>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Stack>
    </Box>
  );
}

export default CampaignBuilderPage;
